import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../services/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import RexiPopup from '../../../components/RexiPopup';
import RexiTooltip from '../../../components/RexiTooltip';
import SmartPagination from '../../../components/SmartPagination';
import { generateStructuredPDF } from '../../../utils/pdfExport';
import '../DataTableTemplate.css';
import './ProductCertification.css';

interface ProductCertificationData {
  id: number;
  product_id?: number;
  auth_id?: string;
  version_id?: string;
  status?: 'Active' | 'Expired';
  manufacture_date?: string;
  product?: {
    id: number;
    upc?: number;
    name?: string;
    certificate_type?: 'GCC' | 'CPC';
  };
}

interface TestReport {
  id: number;
  report_number?: string;
  report_date?: string;
  factory_id?: number;
  factory?: {
    id: number;
    name: string;
  };
}

interface CitationCode {
  id: number;
  citation_code?: string;
  citation_code_description?: string;
  applies_to_gcc?: boolean;
  applies_to_cpc?: boolean;
}

interface Product {
  id: number;
  upc?: number;
  name?: string;
  status: 'active' | 'inactive';
  certificate_type?: 'GCC' | 'CPC';
}

interface ValidationData {
  missingFields: { [category: string]: string[] };
  exportType: 'pdf' | 'csv';
  certification: ProductCertificationData;
}

// Moved outside component to prevent recreation on every render
const FilterInput = ({ label, placeholder, column, value, onChange }: {
  label: string;
  placeholder: string;
  column: string;
  value: string;
  onChange: (column: string, value: string) => void;
}) => (
  <div className="filter-group">
    <label className="filter-label">{label}</label>
    <input
      type="text"
      className="filter-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(column, e.target.value)}
    />
  </div>
);

const ProductCertification = () => {
  const { user } = useAuth();
  const [productCertifications, setProductCertifications] = useState([]);
  const [testReports, setTestReports] = useState([]);
  const [selectedTestReports, setSelectedTestReports] = useState([]);
  const [availableProductsFromReports, setAvailableProductsFromReports] = useState([]);
  const [additionalTestReports, setAdditionalTestReports] = useState([]);
  const [availableAdditionalReports, setAvailableAdditionalReports] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalStage, setModalStage] = useState(1); // 1 = initial selection, 2 = additional reports
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    upc: '',
    product_name: '',
    version_id: '',
    manufacture_date_from: '',
    manufacture_date_to: '',
    status: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const [popupState, setPopupState] = useState({
    help: false,
    delete: false,
    modal: false,
    foreignKeyError: false,
    validation: false,
    attestation: false,
    cpscReminders: false
  });
  const [certificationToDelete, setCertificationToDelete] = useState(null);
  const [validationData, setValidationData] = useState(null as ValidationData | null);
  const [attestationData, setAttestationData] = useState(null as { type: 'single' | 'bulk', certification?: ProductCertificationData } | null);

  const [editingCertification, setEditingCertification] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    product_id: '',
    status: 'Active' as 'Active' | 'Expired'
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [hoveredTooltip, setHoveredTooltip] = useState(null as string | null);
  const [maxLabCount, setMaxLabCount] = useState(0);

  // Force close any tooltips on component mount/refresh
  useEffect(() => {
    setHoveredTooltip(null);
  }, []);

  // Fetch product certifications with manufacture dates from Supabase
  const fetchProductCertifications = async () => {
    try {
      setLoading(true);
      // Try the same pattern as test reports but for single product relationship
      const { data, error } = await supabase
        .from('product_certification')
        .select(`
          id,
          product_id,
          auth_id,
          version_id,
          status,
          products!product_certification_product_id_fkey (id, upc, name, certificate_type)
        `)
        .eq('auth_id', user?.id)
        .order('id', { ascending: false });

      if (error) {
        console.error('Error with foreign key syntax, trying manual join:', error);
        
        // Fallback to manual join if the foreign key relationship isn't set up
        const { data: certifications, error: certError } = await supabase
          .from('product_certification')
          .select('id, product_id, auth_id, version_id, status')
          .eq('auth_id', user?.id)
          .order('id', { ascending: false });

        if (certError) throw certError;

        const { data: products, error: prodError } = await supabase
          .from('products')
          .select('id, upc, name, certificate_type')
          .eq('auth_id', user?.id);

        if (prodError) throw prodError;

        // Manually join the data and calculate manufacture dates
        const joinedData = await Promise.all((certifications || []).map(async cert => {
          const product = products?.find(p => p.id === cert.product_id);
          const manufactureDate = await getManufactureDate(cert.id);
          return {
            ...cert,
            product: product || null,
            manufacture_date: manufactureDate
          };
        }));

        console.log('Product certifications data (manual join):', joinedData);
        setProductCertifications(joinedData);
        return;
      }

      // Calculate manufacture dates for foreign key data
      const dataWithManufactureDates = await Promise.all((data || []).map(async cert => {
        const manufactureDate = await getManufactureDate(cert.id);
        return {
          ...cert,
          manufacture_date: manufactureDate
        };
      }));

      console.log('Product certifications data (foreign key):', dataWithManufactureDates);
      setProductCertifications(dataWithManufactureDates);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching product certifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate manufacture date as the oldest test report date for a certification
  const getManufactureDate = async (certificationId: number): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('product_certification_test_reports')
        .select(`
          test_reports (report_date)
        `)
        .eq('product_certification_id', certificationId);

      if (error || !data || data.length === 0) {
        return null;
      }

      // Find the oldest (minimum) report date
      const reportDates = data
        .map((item: any) => item.test_reports?.report_date)
        .filter(Boolean)
        .sort();

      return reportDates.length > 0 ? reportDates[0] : null;
    } catch (err: any) {
      console.error('Error fetching manufacture date:', err);
      return null;
    }
  };

  // Fetch test reports for the dropdown
  const fetchTestReports = async () => {
    try {
      const { data, error } = await supabase
        .from('test_reports')
        .select(`
          id,
          report_number,
          report_date,
          factory_id,
          factories (id, name)
        `)
        .eq('auth_id', user?.id)
        .order('report_date', { ascending: false });

      if (error) {
        throw error;
      }

      setTestReports(data || []);
    } catch (err: any) {
      console.error('Error fetching test reports:', err);
    }
  };

  // Fetch test reports associated with a certification for editing
  const fetchCertificationTestReports = async (certificationId: number, productId?: number) => {
    try {
      const { data, error } = await supabase
        .from('product_certification_test_reports')
        .select(`
          test_report_id,
          test_reports (id, report_number, report_date, factory_id, factories (id, name))
        `)
        .eq('product_certification_id', certificationId);

      if (error) {
        throw error;
      }

      const associatedReports = data?.map((item: any) => item.test_reports).filter(Boolean) || [];

      // For edit mode, set the first report as the "initial" report and the rest as "additional"
      if (associatedReports.length > 0) {
        setSelectedTestReports([associatedReports[0]]);
        setAdditionalTestReports(associatedReports.slice(1));
      } else {
        setSelectedTestReports([]);
        setAdditionalTestReports([]);
      }

      // After setting the test reports state, fetch additional available reports if productId provided
      if (productId) {
        // Pass the reports directly to avoid timing issues with state updates
        const selectedReports = associatedReports.length > 0 ? [associatedReports[0]] : [];
        const additionalReports = associatedReports.length > 0 ? associatedReports.slice(1) : [];
        fetchAdditionalTestReportsForProduct(productId, selectedReports, additionalReports);
      }
    } catch (err: any) {
      console.error('Error fetching certification test reports:', err);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchProductCertifications();
      fetchTestReports();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (productCertifications.length > 0) {
      const max = Math.max(...productCertifications.map((cert: any) => cert.labs?.length || 0));
//      setMaxLabCount(max);
    }
  }, [productCertifications]);

  // Helper functions for popup management
  const togglePopup = (popup: keyof typeof popupState, show: boolean) => {
    setPopupState(() => {
      const allClosed = {
        help: false,
        delete: false,
        modal: false,
        foreignKeyError: false,
        validation: false,
        attestation: false,
        cpscReminders: false
      };
      return show ? { ...allClosed, [popup]: true } : allClosed;
    });
  };

  // Table configuration - drives both headers and data rendering
  const tableColumns = [
    { key: 'upc', label: 'UPC', sortable: false, className: 'upc', showInTable: true },
    { key: 'product_name', label: 'Product Name', sortable: false, className: 'product_name', showInTable: true },
    { key: 'version_id', label: 'Version\nID', sortable: true, className: 'version_id', showInTable: true },
    { key: 'manufacture_date', label: 'Manufacture\nDate', sortable: true, className: 'manufacture_date', showInTable: true },
    { key: 'status', label: 'Status', sortable: true, className: 'status', showInTable: true },
    { key: 'certificate_pdf', label: 'Certificate\nPDF', sortable: false, className: 'certificate_pdf', showInTable: true },
    { key: 'certificate_csv', label: 'Certificate\nCSV', sortable: false, className: 'certificate_csv', showInTable: true },
    { key: 'actions', label: 'Actions', sortable: false, className: 'actions', showInTable: true }
  ];

  // Filter product certifications based on column filters
  const filteredProductCertifications = productCertifications.filter((cert: ProductCertificationData) => {
    const matchesUPC = !columnFilters.upc || 
      cert.product?.upc?.toString().includes(columnFilters.upc);
    
    const matchesProductName = !columnFilters.product_name || 
      cert.product?.name?.toLowerCase().includes(columnFilters.product_name.toLowerCase());
    
    const matchesVersionId = !columnFilters.version_id || 
      cert.version_id?.toLowerCase().includes(columnFilters.version_id.toLowerCase());
    
    const matchesStatus = columnFilters.status === 'all' || cert.status === columnFilters.status;
    
    // Manufacture date range filtering
    const matchesManufactureDateFrom = !columnFilters.manufacture_date_from || 
      (cert.manufacture_date && cert.manufacture_date >= columnFilters.manufacture_date_from);
    
    const matchesManufactureDateTo = !columnFilters.manufacture_date_to || 
      (cert.manufacture_date && cert.manufacture_date <= columnFilters.manufacture_date_to);
    
    return matchesUPC && matchesProductName && matchesVersionId && matchesStatus && 
           matchesManufactureDateFrom && matchesManufactureDateTo;
  });

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters((prev: typeof columnFilters) => ({ ...prev, [column]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearAllFilters = () => {
    setColumnFilters({
      upc: '',
      product_name: '',
      version_id: '',
      manufacture_date_from: '',
      manufacture_date_to: '',
      status: 'all'
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reusable interactive cell component
  const InteractiveCell = ({ children, className = "", dataLabel = "" }: any) => (
    <div className={`table-cell ${className}`} data-label={dataLabel}>
      <div
        className="table-cell-input"
        contentEditable
        suppressContentEditableWarning={true}
        onInput={(e: any) => e.preventDefault()}
        onKeyDown={(e: any) => {
          const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Escape'];
          const isCopyOperation = e.ctrlKey && (e.key === 'c' || e.key === 'a');
          if (!allowedKeys.includes(e.key) && !isCopyOperation) {
            e.preventDefault();
          }
        }}
        onPaste={(e: any) => e.preventDefault()}
        onMouseDown={(e: any) => e.stopPropagation()}
        onBlur={(e: any) => e.currentTarget.scrollLeft = 0}
      >
        {children}
      </div>
    </div>
  );

  // Reusable table header cell component
  const TableHeaderCell = ({ column }: any) => {
    const getTooltipMessage = (columnKey: string) => {
      switch (columnKey) {
        case 'version_id':
          return 'The Version ID, required for eFiling, is automatically created, starting at v1 for the first time you certify your product and advancing (v2... v3...) for each future certification.';
        case 'manufacture_date':
          return 'The Manufature Date is automatically determined as the earliest test report date for test reports tied to a certification. It is the oldest test date that determines certificate expiration.';
        case 'status':
          return 'When a certification record will no longer be submitted to your broker due to expiration, change Status to Expired.';
        default:
          return null;
      }
    };

    const tooltipMessage = getTooltipMessage(column.key);

    return (
      <div className={`table-cell ${column.className || ''}`}>
        {tooltipMessage ? (
          <RexiTooltip
            message={tooltipMessage}
            visible={hoveredTooltip === column.key}
            position="top"
          >
            <span
              onMouseEnter={() => setHoveredTooltip(column.key)}
              onMouseLeave={() => setHoveredTooltip(null)}
              style={{ display: 'inline-block' }}
            >
              {column.sortable ? (
                <div className="sortable-header" onClick={() => handleSort(column.key)}>
                  {column.label}
                  <span className={`sort-icon ${getSortIcon(column.key)} ${sortConfig.key === column.key ? 'active' : ''}`}></span>
                </div>
              ) : (
                column.label
              )}
            </span>
          </RexiTooltip>
        ) : (
          <>
            {column.sortable ? (
              <div className="sortable-header" onClick={() => handleSort(column.key)}>
                {column.label}
                <span className={`sort-icon ${getSortIcon(column.key)} ${sortConfig.key === column.key ? 'active' : ''}`}></span>
              </div>
            ) : (
              column.label
            )}
          </>
        )}
      </div>
    );
  };

  // Reusable table data cell component
  const TableDataCell = ({ column, certification }: any) => {
    if (column.key === 'actions') {
      return (
        <div className="table-cell" data-label="Actions">
          <div className="table-actions">
            <button className="table-action-btn table-edit-btn" onClick={() => openEditModal(certification)}>
              Details
            </button>
            <button className="table-action-btn table-delete-btn" onClick={() => confirmDelete(certification)}>
              Delete
            </button>
          </div>
        </div>
      );
    }

    if (column.key === 'status') {
      return (
        <InteractiveCell className={column.className || ''} dataLabel={column.label}>
          {certification.status || 'Active'}
        </InteractiveCell>
      );
    }


    if (column.key === 'upc') {
      return (
        <InteractiveCell className={column.className || ''} dataLabel={column.label}>
          {certification.product?.upc || '-'}
        </InteractiveCell>
      );
    }

    if (column.key === 'product_name') {
      return (
        <InteractiveCell className={column.className || ''} dataLabel={column.label}>
          <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: '1.3' }}>
            {certification.product?.name || '-'}
          </div>
        </InteractiveCell>
      );
    }

    if (column.key === 'version_id') {
      return (
        <InteractiveCell className={column.className || ''} dataLabel={column.label}>
          {certification.version_id || 'v1'}
        </InteractiveCell>
      );
    }

    if (column.key === 'manufacture_date') {
      return (
        <InteractiveCell className={column.className || ''} dataLabel={column.label}>
          {certification.manufacture_date ? new Date(certification.manufacture_date + 'T00:00:00').toLocaleDateString() : '-'}
        </InteractiveCell>
      );
    }

    if (column.key === 'certificate_pdf') {
      return (
        <div className="table-cell certificate_pdf" data-label="Certificate PDF">
          <button 
            className="certificate-link"
            onClick={() => handleDownloadCertificate(certification)}
          >
            Download PDF
          </button>
        </div>
      );
    }

    if (column.key === 'certificate_csv') {
      return (
        <div className="table-cell certificate_csv" data-label="Certificate CSV">
          <button 
            className="certificate-link"
            onClick={() => handleDownloadCertificateCSV(certification)}
          >
            Export CSV
          </button>
        </div>
      );
    }

    // Regular interactive cell
    return (
      <InteractiveCell className={column.className || ''} dataLabel={column.label}>
        {(certification as any)[column.key] || '-'}
      </InteractiveCell>
    );
  };

  const hasActiveFilters = Object.values(columnFilters).some(filter => filter && filter !== 'all');

  // Sorting functionality
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to filtered results
  const sortedProductCertifications = useMemo(() => {
    if (!sortConfig.key) return filteredProductCertifications;

    return [...filteredProductCertifications].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      if (sortConfig.key === 'id') {
        aValue = a.id || 0;
        bValue = b.id || 0;
      } else if (sortConfig.key === 'upc') {
        aValue = a.product?.upc || 0;
        bValue = b.product?.upc || 0;
      } else if (sortConfig.key === 'product_name') {
        aValue = (a.product?.name || '').toLowerCase();
        bValue = (b.product?.name || '').toLowerCase();
      } else if (sortConfig.key === 'manufacture_date') {
        aValue = a.manufacture_date || '';
        bValue = b.manufacture_date || '';
      } else {
        aValue = (a[sortConfig.key] || '').toString().toLowerCase();
        bValue = (b[sortConfig.key] || '').toString().toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredProductCertifications, sortConfig]);

  // Apply pagination to sorted results
  const paginatedProductCertifications = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedProductCertifications.slice(startIndex, endIndex);
  }, [sortedProductCertifications, currentPage, itemsPerPage]);

  // Calculate pagination info
  const totalPages = Math.ceil(sortedProductCertifications.length / itemsPerPage);
  const totalItems = sortedProductCertifications.length;

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return 'none';
    }
    return sortConfig.direction;
  };

  // Handle delete product certification
  const handleDeleteProductCertification = async () => {
    if (!certificationToDelete) return;

    try {
      // First, delete related records in the junction table
      const { error: deleteJunctionError } = await supabase
        .from('product_certification_test_reports')
        .delete()
        .eq('product_certification_id', certificationToDelete.id)
        .eq('auth_id', user?.id);

      if (deleteJunctionError) {
        throw new Error(`Error deleting test report associations: ${deleteJunctionError.message}`);
      }

      // Then delete the product certification record
      const { error } = await supabase
        .from('product_certification')
        .delete()
        .eq('id', certificationToDelete.id)
        .eq('auth_id', user?.id);

      if (error) {
        throw error;
      }

      setProductCertifications(productCertifications.filter((c: ProductCertificationData) => c.id !== certificationToDelete.id));
      togglePopup('delete', false);
      setCertificationToDelete(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Error deleting product certification:', err);
    }
  };

  const confirmDelete = (certification: ProductCertificationData) => {
    setCertificationToDelete(certification);
    togglePopup('delete', true);
  };

  // Modal functions
  const openAddModal = () => {
    setEditingCertification(null);
    setFormData({
      product_id: '',
      status: 'Active'
    });
    setSelectedTestReports([]);
    setAdditionalTestReports([]);
    setAvailableAdditionalReports([]);
    setSelectedProduct(null);
    setModalStage(1);
    setFormErrors({});
    togglePopup('modal', true);
  };

  const openEditModal = async (certification: ProductCertificationData) => {
    setEditingCertification(certification);
    setFormData({
      product_id: certification.product_id?.toString() || '',
      status: certification.status || 'Active'
    });
    setSelectedTestReports([]);
    setAdditionalTestReports([]);
    setAvailableAdditionalReports([]);
    setSelectedProduct(null);
    setModalStage(1);
    setFormErrors({});
    
    // Set the selected product for edit mode
    if (certification.product) {
      setSelectedProduct(certification.product);
      setFormData({
        product_id: certification.product_id?.toString() || '',
        status: certification.status || 'Active'
      });
      setModalStage(2);
      
      // Fetch associated test reports for this certification
      if (certification.id) {
        await fetchCertificationTestReports(certification.id, certification.product.id);
        // The fetchCertificationTestReports function will trigger fetching additional reports
        // after the state is updated
      }
    }
    
    togglePopup('modal', true);
  };

  const closeModal = () => {
    togglePopup('modal', false);
    setEditingCertification(null);
    setSelectedTestReports([]);
    setAdditionalTestReports([]);
    setAvailableProductsFromReports([]);
    setAvailableAdditionalReports([]);
    setSelectedProduct(null);
    setModalStage(1);
    setFormErrors({});
    setSaving(false);
  };

  // Fetch products from selected test reports
  const fetchProductsFromTestReports = async (testReportIds: number[]) => {
    if (testReportIds.length === 0) {
      setAvailableProductsFromReports([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('test_report_products')
        .select(`
          product_id,
          products (id, upc, name, status, certificate_type)
        `)
        .in('test_report_id', testReportIds);

      if (error) throw error;

      // Get unique products
      const uniqueProducts = new Map<number, Product>();
      data?.forEach((item: any) => {
        if (item.products) {
          uniqueProducts.set(item.products.id, item.products);
        }
      });

      setAvailableProductsFromReports(Array.from(uniqueProducts.values()));
    } catch (err: any) {
      console.error('Error fetching products from test reports:', err);
      setAvailableProductsFromReports([]);
    }
  };

  // Fetch additional test reports that contain the selected product
  const fetchAdditionalTestReportsForProduct = async (productId: number, overrideSelectedReports?: TestReport[], overrideAdditionalReports?: TestReport[]) => {
    if (!productId) {
      setAvailableAdditionalReports([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('test_report_products')
        .select(`
          test_report_id,
          test_reports (
            id,
            report_number,
            report_date,
            factory_id,
            factories (id, name)
          )
        `)
        .eq('product_id', productId);

      if (error) throw error;

      // Get unique test reports that contain this product
      const uniqueReports = new Map<number, TestReport>();
      data?.forEach((item: any) => {
        if (item.test_reports) {
          uniqueReports.set(item.test_reports.id, item.test_reports);
        }
      });

      // Filter out already selected test reports - use override parameters if provided
      const currentSelectedReports = overrideSelectedReports || selectedTestReports;
      const currentAdditionalReports = overrideAdditionalReports || additionalTestReports;
      const allSelectedReports = [...currentSelectedReports, ...currentAdditionalReports];
      
      const availableReports = Array.from(uniqueReports.values()).filter(
        (report: TestReport) => !allSelectedReports.some((selected: TestReport) => selected.id === report.id)
      );

      setAvailableAdditionalReports(availableReports);
      return availableReports;
    } catch (err: any) {
      console.error('Error fetching additional test reports for product:', err);
      setAvailableAdditionalReports([]);
      return [];
    }
  };

  // Test report management functions
  const addTestReport = (testReportId: number) => {
    const testReport = testReports.find((tr: TestReport) => tr.id === testReportId);
    if (!testReport) return;

    // For stage 1, replace the selection (only one test report allowed)
    const newSelectedReports = [testReport];
    setSelectedTestReports(newSelectedReports);
    // Clear product selection when test reports change
    setFormData((prev: typeof formData) => ({ ...prev, product_id: '' }));
    // Clear any previous additional test report selections when changing initial test reports
    setAdditionalTestReports([]);
    setAvailableAdditionalReports([]);
    // Fetch products from updated test reports
    fetchProductsFromTestReports(newSelectedReports.map((tr: TestReport) => tr.id));
  };

  // Remove test report from add modal - only removes the specific report
  const removeTestReportFromAdd = (testReportId: number) => {
    const newSelectedReports = selectedTestReports.filter((tr: TestReport) => tr.id !== testReportId);
    setSelectedTestReports(newSelectedReports);
    
    // If no reports remain in selectedTestReports, handle appropriately
    if (newSelectedReports.length === 0) {
      // If there are still additional test reports, we need to promote one to be the primary
      if (additionalTestReports.length > 0) {
        // Move the first additional report to selectedTestReports
        const firstAdditionalReport = additionalTestReports[0];
        const remainingAdditionalReports = additionalTestReports.slice(1);
        
        setSelectedTestReports([firstAdditionalReport]);
        setAdditionalTestReports(remainingAdditionalReports);
        
        // Keep the current selected product - don't clear it since additional reports
        // are for the same product as the original selected reports
        // The modal should remain functional with the promoted report
      } else {
        // No reports left at all, clear everything and go back to stage 1
        setFormData((prev: typeof formData) => ({ ...prev, product_id: '' }));
        setSelectedProduct(null);
        setModalStage(1);
        setAvailableProductsFromReports([]);
      }
    } else {
      // Still have selected reports, clear product selection and refetch
      setFormData((prev: typeof formData) => ({ ...prev, product_id: '' }));
      setSelectedProduct(null);
      fetchProductsFromTestReports(newSelectedReports.map((tr: TestReport) => tr.id));
    }
  };

  // Remove test report from edit modal - simplified, preserves product context
  const removeTestReportFromEdit = (testReportId: number) => {
    const newSelectedReports = selectedTestReports.filter((tr: TestReport) => tr.id !== testReportId);
    setSelectedTestReports(newSelectedReports);
    // Note: UI prevents removing the last test report, so we don't need zero-report handling
  };


  // Handle product selection and advance to stage 2 (or stay on stage 1 if no additional reports)
  const handleProductSelection = async (productId: string) => {
    if (!productId) return;
    
    // Ensure complete clean slate before advancing
    setAdditionalTestReports([]);
    setAvailableAdditionalReports([]);
    
    const product = availableProductsFromReports.find((p: Product) => p.id === parseInt(productId));
    if (product) {
      setSelectedProduct(product);
      setFormData((prev: typeof formData) => ({ ...prev, product_id: productId }));
      
      // Fetch additional test reports and wait for the result
      const availableReports = await fetchAdditionalTestReportsForProduct(parseInt(productId));
      
      // If there are additional test reports available, advance to stage 2
      // Otherwise, stay on stage 1 with final buttons
      if (availableReports && availableReports.length > 0) {
        setModalStage(2);
      }
      // If no additional reports, stay on stage 1 - the final buttons will show
    }
  };

  // Add additional test report
  const addAdditionalTestReport = (testReportId: number) => {
    const testReport = availableAdditionalReports.find((tr: TestReport) => tr.id === testReportId);
    if (!testReport) return;

    // Check if test report is already selected
    if (additionalTestReports.some((str: TestReport) => str.id === testReportId)) return;

    const newAdditionalReports = [...additionalTestReports, testReport];
    setAdditionalTestReports(newAdditionalReports);
    
    // Immediately remove from available list
    const updatedAvailableReports = availableAdditionalReports.filter((r: TestReport) => r.id !== testReportId);
    setAvailableAdditionalReports(updatedAvailableReports);
  };

  // Remove additional test report from add modal
  const removeAdditionalTestReportFromAdd = (testReportId: number) => {
    const newAdditionalReports = additionalTestReports.filter((tr: TestReport) => tr.id !== testReportId);
    setAdditionalTestReports(newAdditionalReports);
    
    // Update available reports list to include the removed one
    if (selectedProduct) {
      fetchAdditionalTestReportsForProduct(selectedProduct.id);
    }
    
    // Note: We don't clear other arrays or reset modal stage from here
    // That should only happen when removing from selectedTestReports
  };

  // Remove additional test report from edit modal - simplified, preserves context
  const removeAdditionalTestReportFromEdit = (testReportId: number) => {
    const newAdditionalReports = additionalTestReports.filter((tr: TestReport) => tr.id !== testReportId);
    setAdditionalTestReports(newAdditionalReports);
    // Note: UI prevents removing the last test report, so we don't need zero-report handling
    
    // Update available reports list to include the removed one
    if (selectedProduct) {
      fetchAdditionalTestReportsForProduct(selectedProduct.id);
    }
  };

  // Return to stage 1 (change product selection)
  const returnToStage1 = () => {
    setSelectedProduct(null);
    setAdditionalTestReports([]);
    setAvailableAdditionalReports([]);
    setAvailableProductsFromReports([]);
    setModalStage(1);
    setFormData((prev: typeof formData) => ({ ...prev, product_id: '' }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Required fields validation
    if (!formData.product_id?.trim()) {
      errors.product_id = 'Product is required';
    }
    
    // Test reports validation - ensure at least one test report is selected
    const totalTestReports = selectedTestReports.length + additionalTestReports.length;
    if (totalTestReports === 0) {
      errors.testReports = 'At least one test report must be selected';
    }
    
    // Stage validation
    if (modalStage === 1 && selectedTestReports.length === 0) {
      errors.testReports = 'Please select an initial test report';
    }
    
    if (modalStage === 1 && !selectedProduct) {
      errors.product_id = 'Please select a product';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProductCertification = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      // Get the selected product to check UPC
      const selectedProduct = availableProductsFromReports.find((product: Product) => 
        product.id === parseInt(formData.product_id)
      );

      let versionId = 'v1'; // Default version

      // Only check for duplicates when creating new certification (not editing)
      if (!editingCertification && selectedProduct?.upc) {
        // Check for existing certifications with the same UPC
        const { data: existingCertifications, error: checkError } = await supabase
          .from('product_certification')
          .select(`
            version_id,
            products!product_certification_product_id_fkey (upc)
          `)
          .eq('auth_id', user?.id);

        if (checkError) {
          // Fallback to manual join if foreign key fails
          const { data: certifications, error: certError } = await supabase
            .from('product_certification')
            .select('id, product_id, version_id')
            .eq('auth_id', user?.id);

          if (certError) throw certError;

          const { data: products, error: prodError } = await supabase
            .from('products')
            .select('id, upc')
            .eq('auth_id', user?.id);

          if (prodError) throw prodError;

          // Find certifications with matching UPC
          const matchingCertifications = (certifications || []).filter((cert) => {
            const product = products?.find(p => p.id === cert.product_id);
            return product?.upc === selectedProduct.upc;
          });

          if (matchingCertifications.length > 0) {
            // Find the highest version number
            const versions = matchingCertifications
              .map(cert => cert.version_id || 'v1')
              .map(version => parseInt(version.replace('v', '')) || 1)
              .sort((a, b) => b - a);
            
            const highestVersion = versions[0] || 1;
            versionId = `v${highestVersion + 1}`;
          }
        } else {
          // Use foreign key data
          const matchingCertifications = (existingCertifications || []).filter((cert: any) => 
            cert.products?.upc === selectedProduct.upc
          );

          if (matchingCertifications.length > 0) {
            // Find the highest version number
            const versions = matchingCertifications
              .map((cert: any) => cert.version_id || 'v1')
              .map(version => parseInt(version.replace('v', '')) || 1)
              .sort((a, b) => b - a);
            
            const highestVersion = versions[0] || 1;
            versionId = `v${highestVersion + 1}`;
          }
        }
      }

      const certificationData = {
        product_id: parseInt(formData.product_id),
        auth_id: user?.id,
        ...(editingCertification ? { status: formData.status } : { version_id: versionId }) // Include status for edits, version_id for new certifications
      };

      if (editingCertification) {
        // Update existing product certification
        const { error } = await supabase
          .from('product_certification')
          .update(certificationData)
          .eq('id', editingCertification.id)
          .eq('auth_id', user?.id);

        if (error) throw error;

        // Delete existing test report associations
        const { error: deleteError } = await supabase
          .from('product_certification_test_reports')
          .delete()
          .eq('product_certification_id', editingCertification.id)
          .eq('auth_id', user?.id);

        if (deleteError) throw deleteError;

        // Insert new test report associations
        const allTestReports = [...selectedTestReports, ...additionalTestReports];
        if (allTestReports.length > 0) {
          const testReportInserts = allTestReports.map((testReport: TestReport) => ({
            product_certification_id: editingCertification.id,
            test_report_id: testReport.id,
            auth_id: user?.id
          }));

          const { error: insertError } = await supabase
            .from('product_certification_test_reports')
            .insert(testReportInserts);

          if (insertError) throw insertError;
        }

        // Refetch data to get updated product information
        await fetchProductCertifications();
      } else {
        // Create new product certification
        const { data, error } = await supabase
          .from('product_certification')
          .insert([certificationData])
          .select('id')
          .single();

        if (error) throw error;

        // Insert test report associations for new certification
        const allTestReports = [...selectedTestReports, ...additionalTestReports];
        if (allTestReports.length > 0) {
          const testReportInserts = allTestReports.map((testReport: TestReport) => ({
            product_certification_id: data.id,
            test_report_id: testReport.id,
            auth_id: user?.id
          }));

          const { error: insertError } = await supabase
            .from('product_certification_test_reports')
            .insert(testReportInserts);

          if (insertError) throw insertError;
        }

        // Refetch data to get updated product information
        await fetchProductCertifications();
      }

      closeModal();
    } catch (err: any) {
      setError(err.message);
      console.error('Error saving product certification:', err);
    } finally {
      setSaving(false);
    }
  };

  // Generate and download CSV for a single certification (called after attestation confirmation)
  const generateSingleCertificationCSV = async (certification: ProductCertificationData) => {
    try {
      const certificateData = await fetchCertificateData(certification);
      
      const upc = certificateData.product?.upc || '';
      const productName = certificateData.product?.name || '';
      const versionId = certification.version_id || 'v1';
      const manufactureDate = certification.manufacture_date
        ? (() => {
          const date = new Date(certification.manufacture_date);
          if (isNaN(date.getTime())) return '';
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `'${month}/${year}`; // <-- Note the single quote prefix
        })()
        : '';

      const status = certification.status || 'Active';

      // Helper function to escape CSV fields
      const escapeCSV = (field: any) => {
        const str = String(field || '');
        return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
      };

      // Create comprehensive CSV content
      const headers = [
        // Certifier Information
        'Certifier ID', 'Company Name', 'Point of Contact', 'Contact Name', 'Contact Phone', 'Contact Email',
        'Contact Address Line 1', 'Contact Address Line 2', 'Contact Suite', 'Contact City',
        'Contact State/Province', 'Contact Country', 'Contact Postal Code',
        
        // Product Information
        'Product ID Type', 'Product ID', 'Product Name', 'Certificate Type', 'Version ID', 'Manufacture Date',
        
        // Factory Information (concatenated for all factories)
        'Factory Name', 'Factory Address', 'Factory City', 'Factory State/Province', 'Factory Country',
        'Factory Phone', 'Factory Email',
      ];

      const labFields = ['Name', 'Type', 'CPSC ID', 'Address', 'City', 'State/Province', 'Country', 'Phone', 'Email', 'Citation Codes'];

      for (let i = 1; i <= certificateData.labs.length; i++) {
        labFields.forEach(field => headers.push(`Lab ${i} ${field}`));
      }

      headers.push(
        // Test Report Information (concatenated for all reports)
        'Last Test Date',

        // Attestation
        'Attestation Provided'
      );

      // Build data row
      const mgmt = certificateData.certificationManagement;
      const factories = certificateData.factories;
      const labs = certificateData.labs;
      const testReports = certificateData.testReports;
      const citations = certificateData.citationCodes;

      const dataRow = [
        // Certifier Information
        mgmt?.certifier_id || '',
        mgmt?.company_name_on_certificates || '',
        'Importer',
        mgmt?.contact_name || '',
        mgmt?.contact_telephone || '',
        mgmt?.contact_email || '',
        mgmt?.contact_address_line_1 || '',
        mgmt?.contact_address_line_2 || '',
        mgmt?.contact_suite || '',
        mgmt?.contact_city || '',
        mgmt?.contact_state_province || '',
        mgmt?.contact_country || '',
        mgmt?.contact_postal_code || '',
        
        // Product Information
        'UPC',
        upc,
        productName,
        certificateData.product?.certificate_type || '',
        versionId,
        manufactureDate,
        
        // Factory Information (concatenated with semicolons)
        factories.map(f => f.name || '').join('; '),
        factories.map(f => {
          const addr = [f.address_line_1, f.address_line_2, f.suite].filter(Boolean).join(', ');
          return addr;
        }).join('; '),
        factories.map(f => f.city || '').join('; '),
        factories.map(f => f.state_province || '').join('; '),
        factories.map(f => f.country || '').join('; '),
        factories.map(f => f.telephone || '').join('; '),
        factories.map(f => f.email || '').join('; '),
      ];

      // Lab Information
      for (let i = 0; i < certificateData.labs.length; i++) {
        const lab = labs[i] || {}; // fallback if fewer labs
        const addr = [lab.address_line_1, lab.address_line_2, lab.suite].filter(Boolean).join(', ');
        dataRow.push(
          lab.name || '',
          lab.type || '',
          lab.cpsc_id || '',
          addr,
          lab.city || '',
          lab.state_province || '',
          lab.country || '',
          lab.telephone || '',
          lab.email || '',
          (lab.citations || []).map((c: CitationCode) => c.citation_code || '').join('; ')
        );
      }

      dataRow.push(
        // Test Report Information (concatenated with semicolons)
//        testReports.map(r => r.report_date ? new Date(r.report_date + 'T00:00:00').toLocaleDateString() : '').join('; '),

        (Array.isArray(testReports) ? testReports : []).reduce((latest, r) => {
          const date = r.report_date ? new Date(r.report_date + 'T00:00:00') : null;
          return date && (!latest || date > latest) ? date : latest;
        }, null)?.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) || '',

        // Attestation
        'Yes'
      );

      const csvContent = [
        headers.map(escapeCSV).join(','),
        dataRow.map(escapeCSV).join(',')
      ].join('\n');

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `certificate_comprehensive_${upc || 'No-UPC'}_${versionId}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error generating comprehensive CSV:', error);
      setError('Failed to generate CSV. Please try again.');
    }
  };

  // Generate and download individual CSV for a certification
  const handleDownloadCertificateCSV = async (certification: ProductCertificationData) => {
    try {
      const certificateData = await fetchCertificateData(certification);
      
      // Validate CPSC mandatory fields first
      const validation = validateCPSCFields(certificateData);
      if (!validation.isValid) {
        setValidationData({
          missingFields: validation.missingFields,
          exportType: 'csv',
          certification: certification
        });
        setAttestationData({ type: 'single', certification: certification });
        togglePopup('validation', true);
        return;
      }
      
      // Show CPSC reminders popup first, then attestation
      setAttestationData({ type: 'single', certification: certification });
      togglePopup('cpscReminders', true);
      return;

    } catch (error) {
      console.error('Error generating comprehensive CSV:', error);
      setError('Failed to generate CSV. Please try again.');
    }
  };

  // Validate CPSC mandatory fields for certificate export
  const validateCPSCFields = (certificateData: any) => {
    const missingFields: { [category: string]: string[] } = {};

    // Test Reports validation
    const testReportIssues: string[] = [];
    if (!certificateData.testReports || certificateData.testReports.length === 0) {
      testReportIssues.push('At least one test report is required');
    } else {
      certificateData.testReports.forEach((report: any, index: number) => {
        const reportPrefix = `Test Report ${index + 1}`;
        if (!report.report_number?.trim()) {
          testReportIssues.push(`${reportPrefix}: Report Number is required`);
        }
        if (!report.report_date) {
          testReportIssues.push(`${reportPrefix}: Report Date is required (used for manufacture date)`);
        }
      });
    }
    if (testReportIssues.length > 0) {
      missingFields['Test Reports'] = testReportIssues;
    }

    // Citation Codes validation
    if (!certificateData.citationCodes || certificateData.citationCodes.length === 0) {
      missingFields['Citation Codes'] = ['At least one citation code is required'];
    }

    // Labs validation (must have either CPSC lab ID or complete user lab info)
    const labIssues: string[] = [];
    if (!certificateData.labs || certificateData.labs.length === 0) {
      labIssues.push('At least one testing lab is required');
    } else {
      certificateData.labs.forEach((lab: any, index: number) => {
        const labPrefix = `Lab ${index + 1}`;
        
        // If it's a CPSC certified lab, just need the CPSC ID
        if (lab.type === 'CPSC Certified') {
          if (!lab.cpsc_id?.trim()) {
            labIssues.push(`${labPrefix}: CPSC ID is required for CPSC certified labs`);
          }
        } else {
          // For user labs, need complete information
          const requiredLabFields = [
            { field: 'name', label: 'Lab Name' },
            { field: 'address_line_1', label: 'Address Line 1' },
            { field: 'city', label: 'City' },
            { field: 'state_province', label: 'State/Province' },
            { field: 'country', label: 'Country' },
            { field: 'telephone', label: 'Phone' },
            { field: 'email', label: 'Email' }
          ];
          
          requiredLabFields.forEach(({ field, label }) => {
            if (!lab[field]?.trim()) {
              labIssues.push(`${labPrefix}: ${label} is required`);
            }
          });
          
//          if (lab.status !== 'Active') {
//            labIssues.push(`${labPrefix}: Status must be Active`);
//          }
        }
      });
    }
    if (labIssues.length > 0) {
      missingFields['Testing Labs'] = labIssues;
    }

    // Product validation
    const productIssues: string[] = [];
    if (!certificateData.product) {
      productIssues.push('Product information is required');
    } else {
      const product = certificateData.product;
      if (!product.upc) {
        productIssues.push('UPC is required');
      }
      if (!product.name?.trim()) {
        productIssues.push('Product Name is required');
      }
      if (!product.certificate_type?.trim()) {
        productIssues.push('Certificate Type is required');
      }
    }
    
    // Check certification status (from the certification record itself)
    if (certificateData.certification?.status !== 'Active') {
      productIssues.push('Product Certification status must be Active');
    }
    
    if (productIssues.length > 0) {
      missingFields['Product Information'] = productIssues;
    }

    // Factories validation
    const factoryIssues: string[] = [];
    if (!certificateData.factories || certificateData.factories.length === 0) {
      factoryIssues.push('At least one factory is required');
    } else {
      certificateData.factories.forEach((factory: any, index: number) => {
        const factoryPrefix = `Factory ${index + 1}`;
        const requiredFactoryFields = [
          { field: 'name', label: 'Factory Name' },
          { field: 'address_line_1', label: 'Address Line 1' },
          { field: 'city', label: 'City' },
          { field: 'state_province', label: 'State/Province' },
          { field: 'country', label: 'Country' },
          { field: 'telephone', label: 'Phone' },
          { field: 'email', label: 'Email' }
        ];
        
        requiredFactoryFields.forEach(({ field, label }) => {
          if (!factory[field]?.trim()) {
            factoryIssues.push(`${factoryPrefix}: ${label} is required`);
          }
        });
        
        if (factory.status !== 'active') {
          factoryIssues.push(`${factoryPrefix}: Status must be Active`);
        }
      });
    }
    if (factoryIssues.length > 0) {
      missingFields['Factories'] = factoryIssues;
    }

    return {
      isValid: Object.keys(missingFields).length === 0,
      missingFields
    };
  };

  // Fetch comprehensive data for certificate generation
  const fetchCertificateData = async (certification: ProductCertificationData) => {
    try {
      // Get certification management data
      const { data: certificationManagement, error: certMgmtError } = await supabase
        .from('certification_management')
        .select('*')
        .eq('auth_id', user?.id)
        .single();

      if (certMgmtError && certMgmtError.code !== 'PGRST116') {
        console.error('Error fetching certification management:', certMgmtError);
      }

      // Get all test reports associated with this certification
      const { data: certTestReports, error: certTestReportsError } = await supabase
        .from('product_certification_test_reports')
        .select('test_report_id')
        .eq('product_certification_id', certification.id)
        .eq('auth_id', user?.id);

      if (certTestReportsError) throw certTestReportsError;

      const testReportIds = (certTestReports || []).map(rel => rel.test_report_id);

      if (testReportIds.length === 0) {
        return {
          product: certification.product,
          testReports: [],
          factories: [],
          labs: [],
          citationCodes: [] as CitationCode[],
          certificationManagement: certificationManagement || null
        };
      }

      // Get detailed test report information
      const { data: testReports, error: testReportsError } = await supabase
        .from('test_reports')
        .select('*')
        .in('id', testReportIds)
        .eq('auth_id', user?.id);

      if (testReportsError) throw testReportsError;

      // Get factory information for each test report
      const factoryIds = Array.from(new Set((testReports || []).map(tr => tr.factory_id).filter(Boolean)));
      const { data: factories, error: factoriesError } = factoryIds.length > 0 ? await supabase
        .from('factories')
        .select('*')
        .in('id', factoryIds)
        .eq('auth_id', user?.id) : { data: [], error: null };

      if (factoriesError) throw factoriesError;

      // Get lab information (both CPSC and user labs)
      const cpscLabIds = Array.from(new Set((testReports || []).map(tr => tr.cpsc_lab_id).filter(Boolean)));
      const userLabIds = Array.from(new Set((testReports || []).map(tr => tr.other_lab_id).filter(Boolean)));

      const cpscLabsPromise = cpscLabIds.length > 0 ? supabase
        .from('certified_labs')
        .select('*')
        .in('id', cpscLabIds) : Promise.resolve({ data: [], error: null });

      const userLabsPromise = userLabIds.length > 0 ? supabase
        .from('test_labs')
        .select('*')
        .in('id', userLabIds)
        .eq('auth_id', user?.id) : Promise.resolve({ data: [], error: null });

      const [cpscLabsResult, userLabsResult] = await Promise.all([cpscLabsPromise, userLabsPromise]);

      if (cpscLabsResult.error) throw cpscLabsResult.error;
      if (userLabsResult.error) throw userLabsResult.error;

      const allLabs = [
        ...(cpscLabsResult.data || []).map(lab => ({ ...lab, type: 'ITL' })),
        ...(userLabsResult.data || []).map(lab => ({ ...lab, type: 'LAB' }))
      ];

      // Get citation codes for test reports
      const { data: reportCitations, error: citationsError } = await supabase
        .from('test_report_citation_codes')
        .select(`
          citation_code_id,
          citation_codes (
            id,
            citation_code,
            citation_code_description,
            applies_to_gcc,
            applies_to_cpc
          )
        `)
        .in('test_report_id', testReportIds)
        .eq('auth_id', user?.id);

      if (citationsError) throw citationsError;

      const citationCodes = (reportCitations || []).map(rc => rc.citation_codes).filter(Boolean).flat();

      return {
        product: certification.product,
        testReports: testReports || [],
        factories: factories || [],
        labs: allLabs,
        citationCodes: (citationCodes || []) as CitationCode[],
        certificationManagement: certificationManagement || null,
        certification: certification
      };

    } catch (error) {
      console.error('Error fetching certificate data:', error);
      throw error;
    }
  };

  // Generate and download PDF certificate
  const handleDownloadCertificate = async (certification: ProductCertificationData) => {
    try {
      const certificateData = await fetchCertificateData(certification);
      
      // Validate CPSC mandatory fields
      const validation = validateCPSCFields(certificateData);
      if (!validation.isValid) {
        setValidationData({
          missingFields: validation.missingFields,
          exportType: 'pdf',
          certification: certification
        });
        togglePopup('validation', true);
        return;
      }
      
      const upc = certificateData.product?.upc || 'No-UPC';
      const versionId = certification.version_id || 'v1';
      const productName = certificateData.product?.name || 'Unnamed Product';
      const status = certification.status || 'Active';
      const manufactureDate = certification.manufacture_date ? 
        new Date(certification.manufacture_date + 'T00:00:00').toLocaleDateString() : 'Not Available';

      // Determine certificate title based on certificate type
      const certificateType = certificateData.product?.certificate_type || 'GCC';
      const certificateTitle = certificateType === 'CPC' 
        ? "Children's Product Certificate" 
        : "General Certificate of Conformity";

      // Build sections for PDF
      const sections: Array<{ title: string; content: string }> = [];

      // Store company name for header
      const companyName = certificateData.certificationManagement?.company_name_on_certificates || null;

      // Add product information
      const productContent = `UPC: ${upc}
Product Name: ${productName}
Certificate Type: ${certificateData.product?.certificate_type || 'N/A'}
Version ID: ${versionId}
Manufacture Date: ${manufactureDate}
Status: ${status}`;

      sections.push({
        title: 'PRODUCT INFORMATION',
        content: productContent
      });

      // Add factory information
      if (certificateData.factories.length > 0) {
        let factoriesContent = '';
        certificateData.factories.forEach((factory, index) => {
          if (index > 0) factoriesContent += '\n\n'; // Add spacing between factories
          
          factoriesContent += `Name: ${factory.name || 'N/A'}`;

          // Add address lines separately
          if (factory.address_line_1) {
            factoriesContent += `\nAddress Line 1: ${factory.address_line_1}`;
          }
          
          // Handle address_line_2 and suite formatting
          if (factory.address_line_2) {
            // Check if address_line_2 contains suite information
            if (factory.address_line_2.toLowerCase().startsWith('suite')) {
              factoriesContent += `\nSuite: ${factory.address_line_2}`;
            } else {
              factoriesContent += `\nAddress Line 2: ${factory.address_line_2}`;
            }
          }
          
          if (factory.suite) {
            factoriesContent += `\nSuite: ${factory.suite}`;
          }

          factoriesContent += `\nCity: ${factory.city || 'N/A'}
State/Province: ${factory.state_province || 'N/A'}
Country: ${factory.country || 'N/A'}
Postal Code: ${factory.postal_code || 'N/A'}
Telephone: ${factory.telephone || 'N/A'}
Email: ${factory.email || 'N/A'}`;
        });

        sections.push({
          title: 'MANUFACTURING FACILITY',
          content: factoriesContent
        });
      }

      // Add testing laboratory information
      if (certificateData.labs.length > 0) {
        let labsContent = '';
        certificateData.labs.forEach((lab, index) => {
          if (index > 0) labsContent += '\n\n'; // Add spacing between labs
          
          labsContent += `Name: ${lab.name || 'N/A'}
CPSC ID: ${lab.type === 'CPSC Certified' ? (lab.cpsc_id || 'N/A') : 'Not CPSC Certified'}`;

          // Add address lines separately
          if (lab.address_line_1) {
            labsContent += `\nAddress Line 1: ${lab.address_line_1}`;
          }
          
          // Handle address_line_2 and suite formatting
          if (lab.address_line_2) {
            // Check if address_line_2 contains suite information
            if (lab.address_line_2.toLowerCase().startsWith('suite')) {
              labsContent += `\nSuite: ${lab.address_line_2}`;
            } else {
              labsContent += `\nAddress Line 2: ${lab.address_line_2}`;
            }
          }
          
          if (lab.suite) {
            labsContent += `\nSuite: ${lab.suite}`;
          }

          labsContent += `\nCity: ${lab.city || 'N/A'}
State/Province: ${lab.state_province || 'N/A'}
Country: ${lab.country || 'N/A'}
Postal Code: ${lab.postal_code || 'N/A'}
Telephone: ${lab.telephone || 'N/A'}
Email: ${lab.email || 'N/A'}`;
        });

        sections.push({
          title: 'TESTING LABORATORY',
          content: labsContent
        });
      }


      // Add citation codes information
      if (certificateData.citationCodes.length > 0) {
        let citationsContent = '';
        (certificateData.citationCodes as CitationCode[]).forEach((code, index) => {
          if (index > 0) citationsContent += '\n\n'; // Add spacing between citations
          
          citationsContent += `Citation ${index + 1}:
Code: ${code.citation_code || 'N/A'}
Description: ${code.citation_code_description || 'N/A'}`;
        });

        sections.push({
          title: 'REGULATORY CITATION CODES',
          content: citationsContent
        });
      }

      // Add certificate metadata section
      const metadataContent = `Certificate Generated: ${new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}

Generated by: Comverex.ai Product Certification System`;

      sections.push({
        title: 'CERTIFICATE METADATA',
        content: metadataContent
      });

      // Add contact information at the bottom (moved from top)
      if (certificateData.certificationManagement) {
        const mgmt = certificateData.certificationManagement;
        let contactContent = `Contact Name: ${mgmt.contact_name || 'N/A'}
Contact Phone: ${mgmt.contact_telephone || 'N/A'}
Contact Email: ${mgmt.contact_email || 'N/A'}`;

        // Add address lines separately
        if (mgmt.contact_address_line_1) {
          contactContent += `\nAddress Line 1: ${mgmt.contact_address_line_1}`;
        }
        
        // Handle address_line_2 and suite formatting
        if (mgmt.contact_address_line_2) {
          // Check if address_line_2 contains suite information
          if (mgmt.contact_address_line_2.toLowerCase().startsWith('suite')) {
            contactContent += `\nSuite: ${mgmt.contact_address_line_2}`;
          } else {
            contactContent += `\nAddress Line 2: ${mgmt.contact_address_line_2}`;
          }
        }
        
        if (mgmt.contact_suite) {
          contactContent += `\nSuite: ${mgmt.contact_suite}`;
        }

        contactContent += `\nCity: ${mgmt.contact_city || 'N/A'}
State/Province: ${mgmt.contact_state_province || 'N/A'}
Country: ${mgmt.contact_country || 'N/A'}
Postal Code: ${mgmt.contact_postal_code || 'N/A'}`;

        sections.push({
          title: 'CONTACT INFORMATION',
          content: contactContent
        });
      }

      // Generate PDF using the new PDF utility with company name
      generateStructuredPDF(
        certificateTitle,
        sections,
        `certificate_${upc}_${versionId}.pdf`,
        companyName
      );

    } catch (error) {
      console.error('Error generating certificate:', error);
      setError('Failed to generate certificate. Please try again.');
    }
  };

  // Handle continuing with export despite validation errors
  const handleContinueExport = async () => {
    if (!validationData) return;
    
    togglePopup('validation', false);
    
    try {
      const certificateData = await fetchCertificateData(validationData.certification);
      
      if (validationData.exportType === 'pdf') {
        // Continue with PDF export (same logic as handleDownloadCertificate)
        const upc = certificateData.product?.upc || 'No-UPC';
        const versionId = validationData.certification.version_id || 'v1';
        const productName = certificateData.product?.name || 'Unnamed Product';
        const status = validationData.certification.status || 'Active';
        const manufactureDate = validationData.certification.manufacture_date ? 
          new Date(validationData.certification.manufacture_date + 'T00:00:00').toLocaleDateString() : 'Not Available';

        // Determine certificate title based on certificate type
        const certificateType = certificateData.product?.certificate_type || 'GCC';
        const certificateTitle = certificateType === 'CPC' 
          ? "Children's Product Certificate" 
          : "General Certificate of Conformity";

        // Build sections for PDF
        const sections: Array<{ title: string; content: string }> = [];

        // Store company name for header
        const companyName = certificateData.certificationManagement?.company_name_on_certificates || null;

        // Add product information
        const productContent = `UPC: ${upc}
Product Name: ${productName}
Version ID: ${versionId}
Manufacture Date: ${manufactureDate}`;

        sections.push({
          title: 'PRODUCT INFORMATION',
          content: productContent
        });

        // Add factory information
        if (certificateData.factories.length > 0) {
          let factoriesContent = '';
          certificateData.factories.forEach((factory, index) => {
            if (index > 0) factoriesContent += '\n\n'; // Add spacing between factories
            
            factoriesContent += `Name: ${factory.name || 'N/A'}`;

            // Add address lines separately
            if (factory.address_line_1) {
              factoriesContent += `\nAddress Line 1: ${factory.address_line_1}`;
            }
            
            // Handle address_line_2 and suite formatting
            if (factory.address_line_2) {
              // Check if address_line_2 contains suite information
              if (factory.address_line_2.toLowerCase().startsWith('suite')) {
                factoriesContent += `\nSuite: ${factory.address_line_2}`;
              } else {
                factoriesContent += `\nAddress Line 2: ${factory.address_line_2}`;
              }
            }
            
            if (factory.suite) {
              factoriesContent += `\nSuite: ${factory.suite}`;
            }

            factoriesContent += `\nCity: ${factory.city || 'N/A'}
State/Province: ${factory.state_province || 'N/A'}
Country: ${factory.country || 'N/A'}
Postal Code: ${factory.postal_code || 'N/A'}
Telephone: ${factory.telephone || 'N/A'}
Email: ${factory.email || 'N/A'}`;
          });

          sections.push({
            title: 'MANUFACTURING FACILITY',
            content: factoriesContent
          });
        }

        // Add testing laboratory information
        if (certificateData.labs.length > 0) {
          let labsContent = '';
          certificateData.labs.forEach((lab, index) => {
            if (index > 0) labsContent += '\n\n'; // Add spacing between labs
            
            labsContent += `Name: ${lab.name || 'N/A'}
CPSC ID: ${lab.type === 'CPSC Certified' ? (lab.cpsc_id || 'N/A') : 'Not CPSC Certified'}`;

            // Add address lines separately
            if (lab.address_line_1) {
              labsContent += `\nAddress Line 1: ${lab.address_line_1}`;
            }
            
            // Handle address_line_2 and suite formatting
            if (lab.address_line_2) {
              // Check if address_line_2 contains suite information
              if (lab.address_line_2.toLowerCase().startsWith('suite')) {
                labsContent += `\nSuite: ${lab.address_line_2}`;
              } else {
                labsContent += `\nAddress Line 2: ${lab.address_line_2}`;
              }
            }
            
            if (lab.suite) {
              labsContent += `\nSuite: ${lab.suite}`;
            }

            labsContent += `\nCity: ${lab.city || 'N/A'}
State/Province: ${lab.state_province || 'N/A'}
Country: ${lab.country || 'N/A'}
Postal Code: ${lab.postal_code || 'N/A'}
Telephone: ${lab.telephone || 'N/A'}
Email: ${lab.email || 'N/A'}`;
          });

          sections.push({
            title: 'TESTING LABORATORY',
            content: labsContent
          });
        }


        // Add citation codes information
        if (certificateData.citationCodes.length > 0) {
          let citationsContent = '';
          (certificateData.citationCodes as CitationCode[]).forEach((code, index) => {
            if (index > 0) citationsContent += '\n\n'; // Add spacing between citations
            
            citationsContent += `Citation ${index + 1}:
Code: ${code.citation_code || 'N/A'}
Description: ${code.citation_code_description || 'N/A'}`;
          });

          sections.push({
            title: 'REGULATORY CITATION CODES',
            content: citationsContent
          });
        }

        // Add contact information at the bottom (moved from top)
        if (certificateData.certificationManagement) {
          const mgmt = certificateData.certificationManagement;
          let contactContent = `Contact Name: ${mgmt.contact_name || 'N/A'}
Contact Phone: ${mgmt.contact_telephone || 'N/A'}
Contact Email: ${mgmt.contact_email || 'N/A'}`;

          // Add address lines separately
          if (mgmt.contact_address_line_1) {
            contactContent += `\nAddress Line 1: ${mgmt.contact_address_line_1}`;
          }
          
          // Handle address_line_2 and suite formatting
          if (mgmt.contact_address_line_2) {
            // Check if address_line_2 contains suite information
            if (mgmt.contact_address_line_2.toLowerCase().startsWith('suite')) {
              contactContent += `\nSuite: ${mgmt.contact_address_line_2}`;
            } else {
              contactContent += `\nAddress Line 2: ${mgmt.contact_address_line_2}`;
            }
          }
          
          if (mgmt.contact_suite) {
            contactContent += `\nSuite: ${mgmt.contact_suite}`;
          }

          contactContent += `\nCity: ${mgmt.contact_city || 'N/A'}
State/Province: ${mgmt.contact_state_province || 'N/A'}
Country: ${mgmt.contact_country || 'N/A'}
Postal Code: ${mgmt.contact_postal_code || 'N/A'}`;

          sections.push({
            title: 'CONTACT INFORMATION',
            content: contactContent
          });
        }

        // Generate PDF using the new PDF utility with company name
        generateStructuredPDF(
          certificateTitle,
          sections,
          `certificate_${upc}_${versionId}.pdf`,
          companyName
        );
        
      } else {
        // Show CPSC reminders popup first, then attestation for CSV export after validation
        togglePopup('cpscReminders', true);
        return;
      }
      
    } catch (error) {
      console.error('Error during export:', error);
      setError('Failed to generate export. Please try again.');
    }
    
    setValidationData(null);
  };

  const handleExportCSV = async () => {
    const dataToExport = sortedProductCertifications;
    setAttestationData({ type: 'bulk' });
  };
  const handleAttestationConfirm = async () => {
    if (!attestationData) return;
    try {
      if (attestationData.type === 'single' && attestationData.certification) {
        await generateSingleCertificationCSV(attestationData.certification);
      }
    } finally {
      setAttestationData(null);
    }
  };

  if (loading) {
    return (
      <div className="data-table-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  const openSequentialPopup = (first: keyof typeof popupState, second: keyof typeof popupState) => {
    togglePopup(first, false);
    setTimeout(() => {
      togglePopup(second, true);
    }, 100); // small delay ensures first popup unmounts before second opens
  };

  return (
    <div className="data-table-container">
      <h1 className="data-table-title">Product Certification</h1>
      <div className="page-help-link" onClick={() => togglePopup('help', true)}>
        Ask Rexi about this page
      </div>

      {error && (
        <div className="error-state">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="product-certification-table-container">
        {/* Filter Toggle and Card */}
        <div className="product-certification-filter-section">
        <div className="filter-section">
        <div className="filter-toggle-header">
          <button 
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '▼' : '▶'} Filter Product Certifications
            {hasActiveFilters && <span className="active-filter-indicator">({Object.values(columnFilters).filter(filter => filter && filter !== 'all').length} active)</span>}
          </button>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              Clear All Filters
            </button>
          )}
        </div>
        
        {showFilters && (
          <div className="table-filters">
            <div className="filters-grid">
              <FilterInput 
                label="UPC" 
                placeholder="Search by UPC..." 
                column="upc" 
                value={columnFilters.upc}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Product Name" 
                placeholder="Search by product name..." 
                column="product_name" 
                value={columnFilters.product_name}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Version ID" 
                placeholder="Search by version ID..." 
                column="version_id" 
                value={columnFilters.version_id}
                onChange={handleColumnFilterChange}
              />
              <div className="filter-group">
                <label className="filter-label">Manufacture Date From</label>
                <input
                  type="date"
                  className="filter-input"
                  value={columnFilters.manufacture_date_from}
                  onChange={(e) => handleColumnFilterChange('manufacture_date_from', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Manufacture Date To</label>
                <input
                  type="date"
                  className="filter-input"
                  value={columnFilters.manufacture_date_to}
                  onChange={(e) => handleColumnFilterChange('manufacture_date_to', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Status</label>
                <select
                  className="filter-select"
                  value={columnFilters.status}
                  onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Table Controls */}

      <div className="product-certification-table-header-controls">
        <button className="add-data-btn" onClick={openAddModal}>
          + Add Product Certification
        </button>
        {/* Temporarily hidden due to complex multi-lab handling */}
        {false && (        
          <button className="export-csv-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
        )}
      </div>

      {/* Product Certification Table */}
      <div className="product-certification-table-wrapper">
        <div className="product-certification-table-scrollable">
          <div className="product-certification-data-table">
          <div className="table-header">
            <div className="product-certification-table-row">
              {tableColumns.filter(col => col.showInTable).map((column) => (
                <TableHeaderCell key={column.key} column={column} />
              ))}
            </div>
          </div>
          
          {sortedProductCertifications.length === 0 ? (
            <div className="product-certification-table-row">
              <div className="empty-table-message">
                {hasActiveFilters ? (
                  <>
                    <h4>No product certifications found</h4>
                    <p>Try adjusting your filter criteria.</p>
                  </>
                ) : (
                  <p style={{color: '#999', fontStyle: 'italic'}}>No product certifications yet</p>
                )}
              </div>
            </div>
          ) : (
            paginatedProductCertifications.map((certification: ProductCertificationData) => (
              <div key={certification.id} className="product-certification-table-row">
                {tableColumns.filter(col => col.showInTable).map((column) => (
                  <TableDataCell key={column.key} column={column} certification={certification} />
                ))}
              </div>
            ))
          )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      <SmartPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        itemType="product certifications"
        onPageChange={handlePageChange}
      />
      </div>

      {/* Help Popup */}
      {popupState.help && (
        <RexiPopup
          message={`Manage your product certications here. This is where all of your data is pulled together for generating cerificate PDF's and also CSV exports for your broker.

Note that the certificate Version ID is automatically created, defaulting to v1 for the first certification of a product.

Note also that Manufacture Date is also automatically created, it defaults to the earliest test report date for test reports tied to a certification.`}
          onClose={() => togglePopup('help', false)}
          confirmText="Got It"
        />
      )}

      {/* Delete Confirmation Popup */}
      {popupState.delete && certificationToDelete && (
        <RexiPopup
          message="Are you sure you want to delete this product certification? This action cannot be undone."
          onClose={() => {
            togglePopup('delete', false);
            setCertificationToDelete(null);
          }}
          onContinue={handleDeleteProductCertification}
          showCancel={true}
        />
      )}

      {/* Validation Error Popup */}
      {popupState.validation && validationData && (
        <RexiPopup
          message={`The following required fields are missing for CPSC compliance:

${Object.entries(validationData.missingFields).map(([category, issues]) => 
  `${category}:\n${(issues as string[]).map((issue: string) => `  • ${issue}`).join('\n')}`
).join('\n\n')}

Do you want to continue with the ${validationData.exportType.toUpperCase()} export anyway?`}
          onClose={() => {
            togglePopup('validation', false);
            setValidationData(null);
          }}
          onContinue={handleContinueExport}
          showCancel={true}
          confirmText="Continue Export"
        />
      )}

      {/* CPSC eFiling Reminders Popup */}
      {popupState.cpscReminders && attestationData && (
        <RexiPopup
          message={`A couple of reminders if you are using this exported CSV for CPSC eFiling.

Assure there have been no Material Changes in product design or production processes since the Manufacture Date. You can ask me for more information about Material Changes on the Ask Rexi page.

For Children's Product Certificates, make sure the product being imported was not manufactured more than 1 year after the Manufacture Date since certificates only cover up to 1 year of production.`}
          onClose={() => {
            togglePopup('cpscReminders', false);
            setAttestationData(null);
          }}

//          onContinue={() => {
//            togglePopup('cpscReminders', false);
//            togglePopup('attestation', true);
//          }}

          onContinue={() => openSequentialPopup('cpscReminders', 'attestation')}

          showCancel={true}
          confirmText="Continue"
        />
      )}

      {/* Attestation Popup */}
      {popupState.attestation && attestationData && (
        <RexiPopup
          message={`For each product in the CSV file, you are indicating the following if it is submitted for CPSC eFiling: "You certify that the finished product covered by this certificate complies with the rules, bans, standards, and regulations stated herein, and that the information in this certificate is true and accurate to the best of your knowledge, information, and belief. You understand and acknowledge that it is a United States federal crime to knowingly and willfully make any materially false, fictitious, or fraudulent statements, representations, or omissions, on this certificate."`}
          onClose={() => {
            togglePopup('attestation', false);
            setAttestationData(null);
          }}
          onContinue={handleAttestationConfirm}
          showCancel={true}
          confirmText="I Agree"
        />
      )}

      {/* Add/Edit Product Certification Modal */}
      {popupState.modal && (
        <div className="data-modal-overlay" onClick={closeModal}>
          <div className="data-modal" onClick={(e) => e.stopPropagation()}>
            <div className="data-modal-header">
              <h2 className="data-modal-title">
                {editingCertification ? 'Product Certification Details' : 'Add New Product Certification'}
              </h2>
              <button className="modal-close-btn" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="data-modal-body">
              <div className="form-grid">
                {modalStage === 1 && (
                  <>
                    {/* Stage 1: Initial Test Report and Product Selection */}
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      
                      <label className="form-label">
                        Test Report <span className="required-indicator">*</span>
                      </label>
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                        <select
                          className="form-select"
                          value={selectedTestReports.length > 0 ? selectedTestReports[0].id.toString() : ""}
                          onChange={(e) => {
                            if (e.target.value) {
                              addTestReport(parseInt(e.target.value));
                            } else {
                              // Handle clearing selection if needed
                              setSelectedTestReports([]);
                              setFormData((prev: typeof formData) => ({ ...prev, product_id: '' }));
                              setAvailableProductsFromReports([]);
                            }
                          }}
                          style={{ flex: 1 }}
                        >
                          <option value="">Select test report...</option>
                          {testReports
                            .map((testReport: TestReport) => (
                              <option key={testReport.id} value={testReport.id}>
                                {testReport.report_number || `Report ${testReport.id}`}
                                {testReport.factory?.name && ` (${testReport.factory.name})`}
                              </option>
                            ))}
                        </select>
                      </div>
                      
                      
                      {formErrors.testReports && <div className="form-error">{formErrors.testReports}</div>}
                    </div>

                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">
                        Product from Test Report <span className="required-indicator">*</span>
                      </label>
                      {selectedTestReports.length === 0 ? (
                        <div style={{ color: '#999', fontStyle: 'italic', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                          Please select a test report first to see available products.
                        </div>
                      ) : availableProductsFromReports.length === 0 ? (
                        <div style={{ color: '#999', fontStyle: 'italic', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                          No products found in the selected test report.
                        </div>
                      ) : (
                        <select
                          className="form-select"
                          value={formData.product_id}
                          onChange={(e) => handleProductSelection(e.target.value)}
                        >
                          <option value="">Select a product from the test report...</option>
                          {availableProductsFromReports.map((product: Product) => (
                            <option key={product.id} value={product.id}>
                              {product.upc ? `UPC: ${product.upc}` : 'No UPC'} - {product.name || 'Unnamed Product'}
                            </option>
                          ))}
                        </select>
                      )}
                      {formErrors.product_id && <div className="form-error">{formErrors.product_id}</div>}
                    </div>

                  </>
                )}

                {modalStage === 2 && selectedProduct && (
                  <>
                    {/* Stage 2: Additional Test Reports for Same Product */}
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      

                      <label className="form-label">
                        Test Reports for This Product
                      </label>
                      
                      {availableAdditionalReports.length === 0 ? (
                        <div style={{ color: '#999', fontStyle: 'italic', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                          No additional test reports available for this product.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                          <select
                            className="form-select"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                addAdditionalTestReport(parseInt(e.target.value));
                                e.target.value = "";
                              }
                            }}
                            style={{ flex: 1 }}
                          >
                            <option value="">Optionally select an additional report...</option>
                            {availableAdditionalReports.map((testReport: TestReport) => (
                              <option key={testReport.id} value={testReport.id}>
                                {testReport.report_number || `Report ${testReport.id}`}
                                {testReport.factory?.name && ` (${testReport.factory.name})`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {(selectedTestReports.length > 0 || additionalTestReports.length > 0) && (
                        <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px', marginBottom: '15px' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Test Reports:</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            {[...selectedTestReports, ...additionalTestReports].map((testReport: TestReport) => (
                              <div 
                                key={testReport.id} 
                                style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '5px 8px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '3px',
                                  color: '#333'
                                }}
                              >
                                <span style={{ color: '#333' }}>
                                  <strong>{testReport.report_number || `Report ${testReport.id}`}</strong>
                                  {testReport.factory?.name && ` (${testReport.factory.name})`}
                                </span>
                                {(() => {
                                  const totalTestReports = selectedTestReports.length + additionalTestReports.length;
                                  const isLastReport = totalTestReports === 1;
                                  const isFromSelectedReports = selectedTestReports.some((report: TestReport) => report.id === testReport.id);
                                  
                                  // Only disable removal of last report in edit mode, not add mode
                                  const shouldDisableRemoval = editingCertification && isLastReport;
                                  const tooltipVisible = shouldDisableRemoval && hoveredTooltip === `remove-${testReport.id}`;
                                  
                                  return (
                                    <RexiTooltip
                                      message="Can't remove the only test report. A product certification must have at least one test report."
                                      visible={tooltipVisible}
                                      position="top"
                                    >
                                      <span
                                        onMouseEnter={() => setHoveredTooltip(`remove-${testReport.id}`)}
                                        onMouseLeave={() => setHoveredTooltip(null)}
                                        style={{ display: 'inline-block' }}
                                      >
                                        <button
                                          type="button"
                                          disabled={shouldDisableRemoval}
                                          onClick={() => {
                                            if (shouldDisableRemoval) return;
                                            
                                            // Use appropriate removal function based on add/edit mode
                                            if (editingCertification) {
                                              // Edit mode - use edit-specific removal functions
                                              if (isFromSelectedReports) {
                                                removeTestReportFromEdit(testReport.id);
                                              } else {
                                                removeAdditionalTestReportFromEdit(testReport.id);
                                              }
                                            } else {
                                              // Add mode - use add-specific removal functions
                                              if (isFromSelectedReports) {
                                                removeTestReportFromAdd(testReport.id);
                                              } else {
                                                removeAdditionalTestReportFromAdd(testReport.id);
                                              }
                                            }
                                          }}
                                          style={{
                                            background: shouldDisableRemoval ? '#6c757d' : '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '3px',
                                            padding: '2px 6px',
                                            cursor: shouldDisableRemoval ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            opacity: shouldDisableRemoval ? 0.6 : 1
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </span>
                                    </RexiTooltip>
                                  );
                                })()}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    </div>
                  </>
                )}

                {/* Status field - only show in edit mode */}
                {editingCertification && (
                  <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
                    <label className="form-label">
                      Status <span className="required-indicator">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData((prev: typeof formData) => ({ 
                        ...prev, 
                        status: e.target.value as 'Active' | 'Expired' 
                      }))}
                      style={{ width: '200px' }}
                    >
                      <option value="Active">Active</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="data-modal-footer">
              <button 
                className="modal-btn modal-btn-cancel" 
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              
              {(modalStage === 2 || (modalStage === 1 && selectedProduct)) && (
                <button 
                  className="modal-btn modal-btn-save" 
                  onClick={handleSaveProductCertification}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (editingCertification ? 'Update Product Certification' : 'Add Product Certification')}
                </button>
              )}
              
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCertification;